import { Trans } from "@lingui/react/macro";
import { i18n } from "@lingui/core";
import { formatCurrency } from "../../../../../../shared/utils/utils";
import {
  formatInvoicePeriodDate,
  getInvoiceStatus,
  getPaymentTypeLabel,
  getShareCompanyName,
  type DocumentBearing,
  type SubscriptionInvoice,
  type SubscriptionInvoiceShare,
} from "./subscriptionInvoiceUtils";

/** The payer of one sub-row of a co-funded period, drawn under its summary. */
export function SharePayerCell({
  share,
  isLast,
  dataIdentifier,
}: {
  share: SubscriptionInvoiceShare;
  isLast: boolean;
  dataIdentifier: string;
}) {
  return (
    <div className="flex items-center gap-2 pl-3">
      <span className="font-mono text-default-300 text-xs">
        {isLast ? "└" : "├"}
      </span>
      <span
        className="text-default-600 text-small"
        data-identifier={dataIdentifier}
      >
        {getShareCompanyName(share)}
      </span>
    </div>
  );
}

export function InvoicePeriodCell({
  invoice,
}: {
  invoice: SubscriptionInvoice;
}) {
  return (
    <div className="flex flex-col">
      <span className="text-small" data-identifier="invoice-period-start">
        {formatInvoicePeriodDate(invoice.periodStart)}
      </span>
      <span
        className="text-tiny text-default-500"
        data-identifier="invoice-period-end"
      >
        <Trans>do</Trans> {formatInvoicePeriodDate(invoice.periodEnd)}
      </span>
    </div>
  );
}

export function InvoiceStatusCell({
  invoice,
}: {
  invoice: SubscriptionInvoice;
}) {
  return (
    <span data-identifier="invoice-status-text">
      {getInvoiceStatus(invoice).text}
    </span>
  );
}

export function InvoicePriceCell({
  invoice,
}: {
  invoice: SubscriptionInvoice;
}) {
  const discount = invoice.discountApplication;

  if (!discount) {
    return (
      <span data-identifier="invoice-price">{invoice.basePriceFormatted}</span>
    );
  }

  const discountLabel =
    discount.discountType === "percentage"
      ? `${discount.discountAmount}%`
      : discount.discountAmountAbsoluteFormatted;

  return (
    <div className="flex flex-col">
      <span
        className="font-medium"
        data-identifier="invoice-price-after-discount"
      >
        {invoice.discountedPriceFormatted}
      </span>
      <span
        className="text-tiny text-default-500"
        data-identifier="invoice-discount-label"
      >
        <Trans>Popust</Trans>: {discountLabel}
      </span>
      <span
        className="text-tiny text-default-400 line-through"
        data-identifier="invoice-price-before-discount"
      >
        {invoice.basePriceFormatted}
      </span>
    </div>
  );
}

export function InvoicePaymentCell({
  document,
  currency,
}: {
  document: DocumentBearing;
  currency: string | undefined;
}) {
  if (!(document.spaceInvoicesDocumentId && document.type === "invoice")) {
    return <span className="text-tiny text-default-400">-</span>;
  }

  return (
    <div className="flex flex-row gap-0.5">
      {document.date && (
        <span className="text-tiny text-default-500">
          {document.documentDateFormatted}
        </span>
      )}
      {document.payments && document.payments.length > 0 ? (
        document.payments.map((payment, idx) => (
          <span key={idx} className="text-tiny text-default-500">
            {getPaymentTypeLabel(payment.type)}:{" "}
            {formatCurrency({
              amount: payment.amount,
              locale: i18n.locale,
              currency: currency ?? "",
            })}
          </span>
        ))
      ) : (
        <span className="text-tiny text-default-500">
          <Trans>Bančno nakazilo: čaka na plačilo</Trans>
        </span>
      )}
    </div>
  );
}

export function InvoiceEstimateSentCell({
  document,
}: {
  document: DocumentBearing;
}) {
  const notificationStatus = document.estimateNotificationStatus ?? "not_sent";

  switch (notificationStatus) {
    case "opened":
      return (
        <span
          className="text-tiny text-success"
          data-identifier="invoice-notification-status"
        >
          <Trans>Odprto</Trans>
        </span>
      );
    case "delivered":
      return (
        <span
          className="text-tiny text-success"
          data-identifier="invoice-notification-status"
        >
          <Trans>Dostavljeno</Trans>
        </span>
      );
    case "sent":
      return (
        <span
          className="text-tiny text-success"
          data-identifier="invoice-notification-status"
        >
          <Trans>Poslano</Trans>
        </span>
      );
    default:
      return (
        <span
          className="text-tiny text-default-400"
          data-identifier="invoice-notification-status"
        >
          -
        </span>
      );
  }
}

function getDocumentStatusText(document: DocumentBearing) {
  if (document.canceled) {
    return document.type === "invoice" ? (
      <Trans>Storniran</Trans>
    ) : (
      <Trans>Uporabljen</Trans>
    );
  }
  switch (document.type) {
    case "estimate":
      return <Trans>Predračun</Trans>;
    case "invoice":
      return <Trans>Račun</Trans>;
    default:
      return <Trans>Izdan</Trans>;
  }
}

export function InvoiceDocumentStatusLabel({
  document,
}: {
  document: DocumentBearing;
}) {
  return (
    <div className="flex flex-col">
      <span
        className={`text-tiny ${document.canceled ? "text-red-500" : "text-success"}`}
      >
        {getDocumentStatusText(document)}
      </span>
      <span
        className="text-tiny text-default-400 font-mono"
        data-identifier="invoice-number"
      >
        {document.number}
      </span>
    </div>
  );
}

export function InvoiceDocumentReadOnlyCell({
  document,
}: {
  document: DocumentBearing;
}) {
  if (!document.spaceInvoicesDocumentId) {
    return null;
  }

  return <InvoiceDocumentStatusLabel document={document} />;
}
